
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Card } from "@/components/ui/card";
import { TestTube } from "lucide-react";

export const CandidateTestSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [testUrl, setTestUrl] = useState("");
  const [jobPostUrl, setJobPostUrl] = useState("");

  const { data: settings, isLoading } = useQuery({
    queryKey: ["testSettings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("candidate_test_settings")
        .select("*")
        .single();

      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    if (settings?.test_url) {
      setTestUrl(settings.test_url);
    }
    if (settings?.job_post_url) {
      setJobPostUrl(settings.job_post_url);
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: async ({ testUrl, jobPostUrl }: { testUrl: string; jobPostUrl: string }) => {
      if (settings?.id) {
        const { error } = await supabase
          .from("candidate_test_settings")
          .update({ test_url: testUrl, job_post_url: jobPostUrl })
          .eq("id", settings.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("candidate_test_settings")
          .insert([{ test_url: testUrl, job_post_url: jobPostUrl }]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testSettings"] });
      toast({
        title: "Success",
        description: "Settings have been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
      console.error("Error updating settings:", error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({ testUrl, jobPostUrl });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <TestTube className="h-5 w-5" />
        <h2 className="text-2xl font-bold">Candidate Test Settings</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="testUrl" className="text-sm font-medium">
            Test URL
          </label>
          <Input
            id="testUrl"
            type="url"
            value={testUrl}
            onChange={(e) => setTestUrl(e.target.value)}
            placeholder="Enter the URL for the online test"
            className="w-full"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="jobPostUrl" className="text-sm font-medium">
            Job Post Link
          </label>
          <Input
            id="jobPostUrl"
            type="url"
            value={jobPostUrl}
            onChange={(e) => setJobPostUrl(e.target.value)}
            placeholder="Enter the URL for the job posting"
            className="w-full"
          />
        </div>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? "Saving..." : "Save Settings"}
        </Button>
      </form>
    </Card>
  );
};
